import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Stats {
  totalCourses: number;
  totalModules: number;
  totalLessons: number;
  totalQuizzes: number;
  coursesByStatus: {
    draft: number;
    published: number;
    archived: number;
  };
  coursesThisMonth: number;
  lessonsThisMonth: number;
  recentCourses: {
    id: string;
    title: string;
    createdAt: string;
    status: string;
    moduleCount: number;
    lessonCount: number;
  }[];
  activityTimeline: {
    date: string;
    action: string;
    item: string;
  }[];
}

const CreationStats: React.FC = () => {
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats/creation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load creation stats');
    } finally {
      setLoading(false);
    }
  };

  const exportToGoogleSheets = () => {
    if (!stats) return;

    // Create CSV content for Google Sheets import
    const now = new Date().toLocaleDateString();

    let csvContent = '';

    // Summary section
    csvContent += 'COURSE CREATION TRACKING TEMPLATE\n';
    csvContent += `Generated: ${now}\n\n`;

    // Overview stats
    csvContent += 'OVERVIEW STATS\n';
    csvContent += 'Metric,Value\n';
    csvContent += `Total Courses,${stats.totalCourses}\n`;
    csvContent += `Total Modules,${stats.totalModules}\n`;
    csvContent += `Total Lessons,${stats.totalLessons}\n`;
    csvContent += `Total Quizzes,${stats.totalQuizzes}\n`;
    csvContent += `Courses This Month,${stats.coursesThisMonth}\n`;
    csvContent += `Lessons This Month,${stats.lessonsThisMonth}\n\n`;

    // Status breakdown
    csvContent += 'STATUS BREAKDOWN\n';
    csvContent += 'Status,Count\n';
    csvContent += `Drafts,${stats.coursesByStatus?.draft || 0}\n`;
    csvContent += `Published,${stats.coursesByStatus?.published || 0}\n`;
    csvContent += `Archived,${stats.coursesByStatus?.archived || 0}\n\n`;

    // Course details
    csvContent += 'COURSE DETAILS\n';
    csvContent += 'Course Title,Status,Modules,Lessons,Created Date\n';
    if (stats.recentCourses) {
      stats.recentCourses.forEach(course => {
        const title = course.title.replace(/,/g, ' '); // Escape commas
        const date = course.createdAt ? new Date(course.createdAt).toLocaleDateString() : 'N/A';
        csvContent += `"${title}",${course.status},${course.moduleCount},${course.lessonCount},${date}\n`;
      });
    }

    csvContent += '\n\n';
    csvContent += '--- TRACKING TEMPLATE ---\n';
    csvContent += 'Use the rows below to track your course creation goals:\n\n';
    csvContent += 'Week,Goal (Courses),Actual (Courses),Goal (Lessons),Actual (Lessons),Notes\n';
    csvContent += 'Week 1,,,,, \n';
    csvContent += 'Week 2,,,,, \n';
    csvContent += 'Week 3,,,,, \n';
    csvContent += 'Week 4,,,,, \n';

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `course-tracking-${now.replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Template downloaded! Open in Google Sheets to start tracking.');
  };

  // Free tier - show upgrade prompt
  if (!loading && user?.tier === 'free') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <nav className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div
                onClick={() => navigate('/dashboard')}
                className="cursor-pointer"
              >
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  The Course Creator's Vault
                </h1>
                <p className="text-xs text-gray-500">by Templimize AI</p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </nav>

        {/* Upgrade Prompt */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-2xl shadow-xl p-12 text-center">
            <div className="text-8xl mb-6">📊</div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Creation Stats Dashboard is a Premium Feature
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Track your course creation progress, see detailed analytics, and monitor your productivity over time!
            </p>

            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="text-3xl mb-2">📚</div>
                <div className="font-semibold">Course Tracking</div>
                <div className="text-sm text-gray-600">See all courses at a glance</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="text-3xl mb-2">📈</div>
                <div className="font-semibold">Progress Analytics</div>
                <div className="text-sm text-gray-600">Monthly creation stats</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="text-3xl mb-2">🎯</div>
                <div className="font-semibold">Activity Timeline</div>
                <div className="text-sm text-gray-600">Track your productivity</div>
              </div>
            </div>

            <button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition"
            >
              Upgrade to Premium - $47/mo
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading your creation stats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back
              </button>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Creation Stats Dashboard
              </h1>
            </div>
            <div className="text-sm text-gray-600">
              {user?.tier === 'premium' && 'Premium Plan'}
              {user?.tier === 'basic' && 'Basic Plan'}
              {user?.tier === 'lifetime' && 'Lifetime Plan'}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📚</span>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.totalCourses || 0}</div>
            <div className="text-sm text-gray-600">Courses Created</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📖</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.totalLessons || 0}</div>
            <div className="text-sm text-gray-600">Lessons Generated</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📝</span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.totalQuizzes || 0}</div>
            <div className="text-sm text-gray-600">Quizzes Created</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📦</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.totalModules || 0}</div>
            <div className="text-sm text-gray-600">Modules Built</div>
          </div>
        </div>

        {/* This Month Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg p-6 text-white">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>📅</span> This Month's Progress
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-4xl font-bold">{stats?.coursesThisMonth || 0}</div>
                <div className="text-sm opacity-90">New Courses</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-4xl font-bold">{stats?.lessonsThisMonth || 0}</div>
                <div className="text-sm opacity-90">New Lessons</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>📊</span> Course Status Breakdown
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                  <span className="text-gray-700">Drafts</span>
                </div>
                <span className="font-semibold">{stats?.coursesByStatus?.draft || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-gray-700">Published</span>
                </div>
                <span className="font-semibold">{stats?.coursesByStatus?.published || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                  <span className="text-gray-700">Archived</span>
                </div>
                <span className="font-semibold">{stats?.coursesByStatus?.archived || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Courses */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🚀</span> Recent Courses
          </h3>
          {stats?.recentCourses && stats.recentCourses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Course Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Modules</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Lessons</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Created</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentCourses.map((course) => (
                    <tr key={course.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{course.title}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          course.status === 'published' ? 'bg-green-100 text-green-700' :
                          course.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {course.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600">{course.moduleCount}</td>
                      <td className="py-3 px-4 text-center text-gray-600">{course.lessonCount}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {new Date(course.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => navigate(`/course/${course.id}`)}
                          className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📚</div>
              <p>No courses created yet. Start creating your first course!</p>
              <button
                onClick={() => navigate('/create-course')}
                className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold"
              >
                Create Course
              </button>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/create-course')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
            >
              <span>+</span> Create New Course
            </button>
            <button
              onClick={exportToGoogleSheets}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
            >
              <span>📊</span> Export to Google Sheets
            </button>
            <button
              onClick={() => navigate('/templates')}
              className="bg-white text-purple-600 border-2 border-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition"
            >
              Browse Templates
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition"
            >
              View All Courses
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Export your stats to a CSV file that opens directly in Google Sheets for tracking and planning.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreationStats;
